/**
 * Pure helpers for normalizing cached/shared query payloads into QueryResultData.
 * Extracted verbatim from AppAnalyticsPanel for reuse and testability.
 */

import type { QueryResultData } from '../components/QueryResult';
import type { CachedQueryResultPayload, SharedAnomalyResult } from '../types';

export function parseNumericValue(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

export function normalizeTopDays(raw: unknown): Array<{ date: string; count: number }> {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const date = typeof (item as { date?: string }).date === 'string'
        ? (item as { date: string }).date
        : undefined;
      const count = parseNumericValue((item as { count?: unknown }).count) ?? 0;
      if (!date) return null;
      return { date, count };
    })
    .filter(Boolean) as Array<{ date: string; count: number }>;
}

export function normalizeBreakdown(raw: unknown): Array<{ name: string; count: number }> {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const nameValue = (item as { name?: unknown }).name;
      const name = typeof nameValue === 'string'
        ? nameValue
        : nameValue != null
          ? String(nameValue)
          : '(unknown)';
      const count = parseNumericValue((item as { count?: unknown }).count) ?? 0;
      return { name, count };
    })
    .filter(Boolean) as Array<{ name: string; count: number }>;
}

export function formatRangeLabel(params: Record<string, unknown> | undefined): string | null {
  if (!params) return null;
  const fromDate = typeof params['fromDate'] === 'string' ? params['fromDate'] as string : undefined;
  const toDate = typeof params['toDate'] === 'string' ? params['toDate'] as string : undefined;
  if (fromDate && toDate) {
    return `${fromDate} → ${toDate}`;
  }
  return null;
}

export function humanizeEventName(event?: string): string {
  if (!event || event === '$ae_session') {
    return 'sessions';
  }
  return event
    .replace(/^[\$]+/, '')
    .replace(/_/g, ' ')
    .trim() || event;
}

export function inferDimension(
  params: Record<string, unknown> | undefined,
  data: Record<string, unknown> | undefined,
  fallback: string
): string {
  if (params && typeof params['dimension'] === 'string') {
    return params['dimension'] as string;
  }
  if (data && typeof data['dimension'] === 'string') {
    return data['dimension'] as string;
  }
  return fallback;
}

export function getDimensionLabel(dimension: string): string {
  switch (dimension) {
    case 'city':
      return 'city';
    case 'region':
      return 'region';
    case 'platform':
      return 'platform';
    case 'version':
      return 'version';
    case 'device':
      return 'device';
    default:
      return 'country';
  }
}

export function convertCachedQueryToResult(
  payload: CachedQueryResultPayload,
  fallbackTimestamp?: string
): QueryResultData | null {
  const toolName = payload.toolName;
  if (!toolName) return null;

  const timestamp = typeof payload.timestamp === 'string'
    ? payload.timestamp
    : fallbackTimestamp || new Date().toISOString();
  const params = (payload.params as Record<string, unknown>) || {};
  const dataRecord = payload.data as Record<string, unknown> | undefined;
  const summary = typeof payload.summary === 'string' ? payload.summary : undefined;
  const rangeLabel = formatRangeLabel(params);
  const rangeSuffix = rangeLabel ? ` (${rangeLabel})` : '';
  const eventName = typeof params['event'] === 'string' ? params['event'] as string : undefined;
  const eventLabel = humanizeEventName(eventName);

  switch (toolName) {
    case 'get_top_days': {
      const days = normalizeTopDays(dataRecord?.['days']);
      const limit = parseNumericValue(params['limit']) ?? (days.length || 10);
      const question = summary || `Top ${limit} days for ${eventLabel}${rangeSuffix}`;
      return {
        type: 'top_days',
        question,
        data: days,
        timestamp,
      };
    }
    case 'get_sessions_by_geo': {
      const dimension = inferDimension(params, dataRecord, 'country');
      const breakdown = normalizeBreakdown(dataRecord?.['breakdown']);
      const question = summary || `Sessions by ${getDimensionLabel(dimension)}${rangeSuffix}`;
      return {
        type: dimension === 'city' ? 'by_city' : 'by_country',
        question,
        data: breakdown,
        timestamp,
      };
    }
    case 'detect_anomalies': {
      if (!dataRecord) return null;
      const raw = dataRecord as {
        anomalies?: Array<{ date?: string; value?: unknown; zscore?: unknown; type?: string }>;
        mean?: unknown;
        stdDev?: unknown;
        dailyValues?: Array<{ date?: string; value?: unknown }>;
      };
      const anomalies = Array.isArray(raw.anomalies)
        ? raw.anomalies
            .map((item) => {
              if (!item || typeof item !== 'object') return null;
              const date = typeof item.date === 'string' ? item.date : undefined;
              if (!date) return null;
              const value = parseNumericValue(item.value) ?? 0;
              const zscore = parseNumericValue(item.zscore) ?? 0;
              const type: 'spike' | 'drop' = item.type === 'drop' ? 'drop' : 'spike';
              return { date, value, zscore, type };
            })
            .filter(Boolean) as SharedAnomalyResult['anomalies']
        : [];
      const dailyValues = Array.isArray(raw.dailyValues)
        ? raw.dailyValues
            .map((entry) => {
              if (!entry || typeof entry !== 'object') return null;
              const date = typeof entry.date === 'string' ? entry.date : undefined;
              if (!date) return null;
              const value = parseNumericValue(entry.value) ?? 0;
              return { date, value };
            })
            .filter(Boolean) as SharedAnomalyResult['dailyValues']
        : [];
      const anomalyData: SharedAnomalyResult = {
        anomalies,
        mean: parseNumericValue(raw.mean) ?? 0,
        stdDev: parseNumericValue(raw.stdDev) ?? 0,
        dailyValues,
      };
      const question = summary || `Anomalies for ${eventLabel}${rangeSuffix}`;
      return {
        type: 'anomaly',
        question,
        data: anomalyData,
        timestamp,
      };
    }
    case 'get_actionable_insights': {
      const recsRaw = Array.isArray((dataRecord as { recommendations?: unknown }).recommendations)
        ? (dataRecord as { recommendations: unknown[] }).recommendations
        : [];
      const insights = recsRaw.map((rec) => {
        if (!rec || typeof rec !== 'object') {
          return { type: 'observation', title: 'Insight', description: '' };
        }
        const type = (rec as { type?: string }).type;
        const normalizedType: 'opportunity' | 'warning' | 'observation' = type === 'opportunity' || type === 'warning'
          ? type
          : 'observation';
        return {
          type: normalizedType,
          title: typeof (rec as { title?: string }).title === 'string'
            ? (rec as { title: string }).title
            : 'Insight',
          description: typeof (rec as { description?: string }).description === 'string'
            ? (rec as { description: string }).description
            : '',
          ...(typeof (rec as { action?: string }).action === 'string'
            ? { action: (rec as { action: string }).action }
            : {}),
        };
      });
      return {
        type: 'insight',
        question: summary || 'Actionable insights',
        data: {
          summary: typeof (dataRecord as { summary?: string }).summary === 'string'
            ? (dataRecord as { summary: string }).summary
            : (summary || ''),
          insights,
          generatedAt: timestamp,
          mode: 'local',
        },
        timestamp,
      };
    }
    case 'get_usage_trend': {
      const seriesRaw = Array.isArray((dataRecord as { series?: unknown[] }).series)
        ? (dataRecord as { series: unknown[] }).series
        : [];
      const eventKey = eventName || '$ae_session';
      const values: Record<string, Record<string, number>> = {
        [eventKey]: {},
      };
      for (const point of seriesRaw) {
        if (!point || typeof point !== 'object') continue;
        const date = typeof (point as { date?: string }).date === 'string'
          ? (point as { date: string }).date
          : typeof (point as { week?: string }).week === 'string'
            ? (point as { week: string }).week
            : undefined;
        if (!date) continue;
        const count = parseNumericValue((point as { count?: unknown }).count) ?? 0;
        values[eventKey][date] = count;
      }
      const sortedDates = Object.keys(values[eventKey]).sort();
      const granularity = typeof (dataRecord as { granularity?: string }).granularity === 'string'
        ? (dataRecord as { granularity: string }).granularity
        : undefined;
      const question = summary || `Usage trend for ${eventLabel}${rangeSuffix}${granularity === 'week' ? ' (weekly)' : ''}`;
      return {
        type: 'trend',
        question,
        data: { series: sortedDates, values },
        timestamp,
      };
    }
    case 'get_platform_breakdown': {
      const dimension = inferDimension(params, dataRecord, 'platform');
      const breakdown = normalizeBreakdown(dataRecord?.['breakdown']);
      const question = summary || `${eventLabel} by ${getDimensionLabel(dimension)}${rangeSuffix}`;
      return {
        type: 'by_country',
        question,
        data: breakdown,
        timestamp,
      };
    }
    case 'get_analytics_metrics': {
      return {
        type: 'conversation',
        question: summary ? 'Analytics summary' : 'Analytics metrics',
        data: {
          answer: summary || 'Analytics metrics retrieved.',
          generatedAt: timestamp,
          mode: 'local',
        },
        timestamp,
      };
    }
    default:
      return null;
  }
}

export function inferBreakdownLabelFromQuestion(question?: string): string | null {
  if (!question) return null;
  const q = question.toLowerCase();
  const mapping: Array<[string, string]> = [
    ['platform', 'platform'],
    ['version', 'version'],
    ['device', 'device'],
    ['region', 'region'],
    ['country', 'country'],
    ['city', 'city'],
  ];
  for (const [keyword, label] of mapping) {
    if (q.includes(keyword)) {
      return label;
    }
  }
  return null;
}

export function describeBreakdownFromResult(result: QueryResultData): string {
  const inferred = inferBreakdownLabelFromQuestion(result.question);
  if (inferred) {
    return inferred;
  }
  return result.type === 'by_country' ? 'country' : 'city';
}

export function summarizeResultForContext(result: QueryResultData | null): string | undefined {
  if (!result) return undefined;
  const data: any = result.data;

  switch (result.type) {
    case 'anomaly':
      if (data?.anomalies?.length) {
        const highlights = data.anomalies
          .slice(0, 3)
          .map((a: any) => `${a.date} ${a.type} (${a.zscore}σ)`)
          .join(', ');
        return `Recent anomalies (${data.anomalies.length} total, avg ${data.mean}/day): ${highlights}`;
      }
      break;
    case 'top_days':
      if (Array.isArray(data) && data.length) {
        const top = data.slice(0, 3).map((item: any) => `${item.date}: ${item.count}`).join(', ');
        return `Top days from previous query: ${top}`;
      }
      break;
    case 'by_country':
    case 'by_city':
      if (Array.isArray(data) && data.length) {
        const breakdown = data.slice(0, 3).map((item: any) => `${item.name || 'unknown'} (${item.count})`).join(', ');
        return `Previous breakdown (${describeBreakdownFromResult(result)}): ${breakdown}`;
      }
      break;
    case 'top_users':
      if (Array.isArray(data) && data.length) {
        const users = data.slice(0, 3).map((item: any) => `${item.userId}: ${item.eventCount} events`).join(', ');
        return `Top users snapshot: ${users}`;
      }
      break;
    case 'insight':
      if (data?.summary) {
        return `Last insights summary: ${data.summary}`;
      }
      break;
    case 'conversation':
      if (data?.answer) {
        return `Previous answer: ${data.answer}`;
      }
      break;
    default:
      break;
  }

  return `${result.question}`;
}
